

















































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, InjectReactive, Watch } from 'vue-property-decorator';

// Shared types
import { Color, IndoorUnitColor, OutdoorUnitColor } from '@coldtec/cos-types';

// Tool types
import { Labels } from '@/types';

// Store types
import {
    SystemSplit,
    ToolSystem,
    ToolUnit,
    SpacesGetter,
    SystemsGetter,
    ToolSpace,
    SystemUnitLocation,
} from '@/store/store.types';

// Used for rendering the images and colors
import SystemImages, { SystemImage } from '../SystemImages.vue';

// Internal components
import SystemColors from '../SystemColors.vue';

/**
 *  Holding the available spaces that should
 *  be forwarded to the space's select.
 */
export interface AvailableSpaces {
    [localSpaceId: string]: string;
}

/**
 *  Holding the static guid of the white color, so it
 *  can easily and securely be found for auto-selecting
 *  the color or rendering the white images by default.
 */
const WHITE_COLOR_STATIC_GUID = 'ccos-static-guid-color-white-ccos-st';

/**
 *  Component that renders the corresponding unit of a system, including
 *  the image(s), title, description and location label.
 */
@Component({
    components: {
        SystemImages,
        SystemColors,
    },
})
export default class SystemUnit extends Vue {

    @InjectReactive('toolSystem')
    private toolSystem!: ToolSystem;

    @Prop({ type: String, required: true })
    private title!: string;

    @Prop({ type: Object, required: true })
    private toolUnit!: ToolUnit;

    /**
     *  Used to easily render the label, based
     *  on the location of the unit.
     */
    private locationLabels: Labels = {
        [SystemUnitLocation.Outdoor]: 'Buitenunit',
        [SystemUnitLocation.Indoor]: 'Binnenunit',
    };

    /**
     *  Holding the available colors of the unit.
     */
    private availableColors: Array<Color> = [];

    /**
     *  Holding the available spaces for this unit,
     *  according to a multi split's layouts.
     */
    private availableSpaces: AvailableSpaces = {};

    /**
     *  Used to get the images to render. Either the images
     *  of the selected color or white by default.
     */
    private get images(): Array<SystemImage> {

        const images: Array<SystemImage> = [];

        /**
         *  Find the correct color to render the images for.
         */
        let color: IndoorUnitColor | null = null;

        for (const unitColor of this.colors) {

            /**
             *  Render the unit's white images if no color
             *  has been selected by default.
             */
            if (!this.toolUnit.color && unitColor.color.guid === WHITE_COLOR_STATIC_GUID) {

                color = unitColor;

                break;

            }

            /**
             *  Render the unit's images in their correct
             *  color if this color has been selected.
             */
            if (unitColor.color.guid === this.toolUnit.color) {

                color = unitColor;

                break;

            }

        }

        /**
         *  Add the images of the color.
         */
        if (color) {

            for (const image of color.images) {

                images.push({
                    title: image.name,
                    src: image.file,
                });

            }

        }

        return images;

    }

    /**
     *  Used to get the description of the unit.
     */
    private get description(): string {

        return this.toolUnit.data.description ?? 'Er is helaas geen omschrijving beschikbaar voor deze unit.';

    }

    /**
     *  Used to get all the colors of the unit.
     */
    private get colors(): Array<IndoorUnitColor | OutdoorUnitColor> {

        return this.toolUnit.data.colors as Array<IndoorUnitColor | OutdoorUnitColor>;

    }

    /**
     *  Used to get the sorted colors of the unit.
     */
    private get sortedColors(): Array<Color> {

        return this.availableColors.sort((colorA: Color, colorB: Color): number => {

            if (!colorA.index || !colorB.index) {

                return 0;

            }

            return colorA.index - colorB.index;

        });

    }

    /**
     *  Used to determine if a color should/can
     *  be selected for the unit.
     */
    private get colorSelection(): boolean {

        return this.availableColors.length > 1;

    }

    /**
     *  Used to determine if a space should be selected for
     *  the unit, which depends on a number of conditions.
     */
    private get spaceSelection(): boolean {

        return !!(
            this.toolUnit.location === SystemUnitLocation.Indoor &&
            this.toolSystem.split === SystemSplit.Multi &&
            this.toolSystem.selected &&
            Object.keys(this.availableSpaces).length
        );

    }

    /**
     *  Construct the available colors and automatically
     *  select the single or white color when created.
     */
    private created(): void {

        /**
         *  Available colors.
         */
        for (const color of this.colors) {

            this.availableColors.push(color.color);

        }

        /**
         *  Available spaces.
         */
        for (const layout of this.toolSystem.layouts || []) {

            if (!layout.indoor_units.includes(this.toolUnit.guid)) {

                continue;

            }

            this.availableSpaces[layout.tool_space_id] = this.$store.getters[SpacesGetter.ADDED][layout.tool_space_id].data.name;

        }

    }

    /**
     *  Watch the selected state of the system and reset the
     *  unit's space and color if deselected or auto
     *  select a space and color if selected.
     */
    @Watch('toolSystem.selected')
    private _systemSelectedUpdate(selected: boolean): void {

        if (!selected) {

            this.toolUnit.toolSpaceId = null;
            this.toolUnit.color = null;

            return;

        }

        this._autoSelectSpace();
        this._autoSelectColor();

    }

    /**
     *  Watch the selection of the unit's color
     *  and emit an event when it changes.
     */
    @Watch('toolUnit.color')
    private _colorUpdated(): void {

        this.$emit('color-update');

    }

    /**
     *  Watch the selection of the unit's space and act
     *  accordingly: setting the correct new local space
     *  id and a space's local id to reset if needed.
     */
    @Watch('toolUnit.toolSpaceId')
    private _spaceUpdated(newToolSpaceId: string | null, oldToolSpaceId: string | null): void {

        /**
         *  Translate an empty new local space id to `null`
         *  to let the update logic work as expected.
         */
        this.toolUnit.toolSpaceId = typeof newToolSpaceId === 'string' && !newToolSpaceId.length
            ? null
            : newToolSpaceId;

        /**
         *  Only reset a space's indoor unit if his new
         *  tool space id is not null, otherwise it would keep
         *  resetting itself.
         */
        this.toolUnit.resetToolSpaceId = newToolSpaceId === null ? null : oldToolSpaceId;

        this.$emit('space-update');

    }

    /**
     *  Used to auto-select the color of the unit: which is the first of the white.
     */
    private _autoSelectColor(): void {

        /**
         *  Auto-select the single available color in case of a single color.
         */
        if (this.availableColors.length === 1) {

            this.toolUnit.color = this.availableColors[0].guid;

            return;

        }

        /**
         *  Auto-select the white color in case of multiple colors.
         */
        for (const color of this.availableColors) {

            if (color.guid === WHITE_COLOR_STATIC_GUID) {

                this.toolUnit.color = color.guid;

                break;

            }

        }

    }

    /**
     *
     *  Used to auto-select the space for this unit,
     *  in case of an inside unit.
     *
     *  (i) If the system to which this unit belongs is a
     *  single split, this single (inside) unit must be
     *  auto-selected for the space: the local id of the
     *  space should be set as `localSpaceId` of the unit.
     *
     */
    private _autoSelectSpace(): void {

        /**
         *  Only auto select a space in case of an inside unit.
         */
        if (this.toolUnit.location === SystemUnitLocation.Outdoor) {

            return;

        }

        /**
         *
         *  Only auto select the single available space
         *  of the unit in case of a multi split.
         *
         *  (i) The space select is also disabled when
         *  only a single space is available for this
         *  inside unit of a multi split
         *
         */
        if (this.toolSystem.split === SystemSplit.Multi) {

            const availableToolSpaceIds: Array<string> = Object.keys(this.availableSpaces);

            if (availableToolSpaceIds.length === 1) {

                this.toolUnit.toolSpaceId = availableToolSpaceIds[0];

            }

            return;

        }

        /**
         *  Find the space for which the system got selected and
         *  set his tool space id as `toolSpaceId` of the unit.
         */
        for (const showingSpace of this.$store.getters[SystemsGetter.SPACES] as Array<ToolSpace>) {

            if (showingSpace.data.system !== this.toolSystem.guid) {

                continue;

            }

            this.toolUnit.toolSpaceId = showingSpace.data.tool_space_id;

        }

    }

}
